/**
 * **标量场的扰动源** —— 每次"往场里扔一个东西"就是一条 `Disturbance`。
 *
 * 一条扰动 = 一套参数 + 一个触发时刻。它在 GPU 上被一个公式展开成
 * 一整片标量场，CPU 这边**完全不算场**，只负责：
 *
 * - 决定什么时候、在哪儿、扔哪一种（生产）
 * - 到点了把它丢掉（回收）
 * - 把还活着的打包成 uniform（喂给 GPU）
 *
 * ⚠️ 这也是为什么 `kind` 是一个**索引数字**而不是字符串：
 * 它要直接进 uniform，而分支判断发生在着色器里。
 * 字符串 → 索引的映射只有 `KIND_INDEX` 一处，着色器里的 `if (kind < 0.5)`
 * 必须和它对得上（改动时两边一起改，`field.test.ts` 会盯着）。
 */

/** 目前实现的几种扰动算法 */
export type DisturbanceKind = 'ripple' | 'swirl' | 'pulse' | 'wave';

export const DISTURBANCE_KINDS: readonly DisturbanceKind[] = ['ripple', 'swirl', 'pulse', 'wave'];

/**
 * 种类 → 着色器里的索引。
 *
 * ⚠️ **顺序就是着色器里 `if (kind < 0.5) ... else if (kind < 1.5) ...` 的顺序。**
 * 在中间插入一种会让已有的索引整体错位 —— 所以只许往后追加，
 * 或者同时改 `shaders.ts`。
 */
export const KIND_INDEX: Readonly<Record<DisturbanceKind, number>> = {
  ripple: 0,
  swirl: 1,
  pulse: 2,
  wave: 3,
};

/** 每种算法的参数。哪个字段是什么意思，取决于 `kind` —— 见下面 `defaultParams` 的注释 */
export interface DisturbanceParams {
  /** 强度。最终会和其他扰动**相加**，所以这是"这一份占多大比重" */
  readonly amp: number;
  /** 空间频率（每逻辑单位的弧度）。波长的倒数 */
  readonly freq: number;
  /** 演化速度。ripple / wave 是传播速度，swirl / pulse 是角速度 */
  readonly speed: number;
  /** 寿命（秒）。到了就回收，同时它也是时间包络的衰减常数 */
  readonly life: number;
  /** 特征半径（逻辑单位）。不同算法含义不同，见下表 */
  readonly radius: number;
  /** 初相 / 方向（弧度） */
  readonly phase: number;
}

export interface Disturbance extends DisturbanceParams {
  readonly kind: DisturbanceKind;
  /** 中心，**归一化到 0..1**（跟着画布尺寸走，换分辨率不会偏） */
  readonly x: number;
  readonly y: number;
  /** 触发时刻（秒，和着色器的 `uTime` 同一条时间轴） */
  readonly bornAt: number;
}

/**
 * 每种算法的手感默认值。
 *
 * | kind | freq | speed | radius | 视觉 |
 * | --- | --- | --- | --- | --- |
 * | `ripple` | 每单位弧度（0.12 → 波长 ≈ 52） | 波前扩散速度 | 环宽的一半 | 一颗石子砸进水里 |
 * | `swirl` | 角向的"臂数" | 旋转角速度 | 半径包络宽度 | 一个慢慢转的漩涡 |
 * | `pulse` | 每单位弧度 | 向外推的角速度 | 半径衰减长度 | 心跳一样的同心波 |
 * | `wave` | 每单位弧度 | 扫过屏幕的速度 | 未用 | 一道扫过去的平面波 |
 */
export function defaultParams(kind: DisturbanceKind): DisturbanceParams {
  switch (kind) {
    case 'ripple':
      return { amp: 0.9, freq: 0.13, speed: 300, life: 2.6, radius: 26, phase: 0 };
    case 'swirl':
      return { amp: 0.75, freq: 3, speed: 2.4, life: 3.4, radius: 300, phase: 0 };
    case 'pulse':
      return { amp: 0.8, freq: 0.055, speed: 5.5, life: 2.4, radius: 380, phase: 0 };
    case 'wave':
      return { amp: 0.5, freq: 0.02, speed: 140, life: 3.2, radius: 0, phase: 0 };
  }
}

/**
 * **Bar 脉冲用哪一种算法。**
 *
 * 评审要的是「一个从屏幕中心向四周扩散的脉冲扰动」。四种里只有 `ripple`
 * 的波前是**按半径往外走**的（`fRipple` 里 `front = speed * age`，
 * 环就长在 `r = front` 上）—— 那正是"扩散"。
 *
 * ⚠️ `pulse` 那一种名字虽然像，但它是**同心驻波**（`sin(freq*r - age*speed)`）：
 * 环的疏密由 `freq` 定死，扩散速度只有 `speed/freq`，看着像水面在原地抖，
 * 而不是"一圈打出去"。想要那个感觉的话，改这一个词就行。
 */
export const BAR_PULSE_KIND: DisturbanceKind = 'ripple';

/**
 * Bar 脉冲的中心，**归一化坐标**。正中心。
 *
 * 单独抽一个常量是因为它是"从屏幕中心向四周扩散"这句话里唯一可测的那一半 ——
 * 测试要能断言"这一发确实打在正中"，而不是从 `Background` 的实现里读出来
 */
export const BAR_PULSE_ORIGIN = 0.5;

/**
 * Bar 脉冲的参数。
 *
 * ⚠️ **这几个数是评审手调的**（原话：「时间倍率和 Bar 参数是我修改的」）。
 * 曾经有一版是"一个 bar 之内扫到边角"（speed 560 / life 1.35 / radius 120），
 * 现在这一版是**又慢又宽又轻**的一圈：`life` 只有半秒（"一下"而不是"一浪"）、
 * `freq` 极低 + `radius` 极大（几千像素）→ 几乎没有环，只有一团很宽很软的
 * 亮起来又退掉。别拿旧的那套推导往回改。
 *
 * 那一圈**只在濒死期间推**（评审：「Bar 触发的扰动是仅在濒死期间生效」），
 * 平常每小节只有"亮一下 + 泛红"（`render/bg/mood.ts` 的 `barFlash`）。
 */
export const BAR_PULSE: DisturbanceParams = {
  amp: 1,
  freq: 0.01,
  speed: 1000,
  life: 0.5,
  radius: 3000,
  phase: 0,
};

/**
 * 到点了没有。`age > life` 就回收。
 *
 * ⚠️ 这个判据必须和着色器里的时间包络**逐字对应**（`age > B.w` 就 skip）。
 * 两边不一致的后果很难查：
 * - CPU 回收得早 → 波纹在半路上突然消失
 * - CPU 回收得晚 → 一条已经看不见的扰动白占着 uniform 槽位，
 *   容量满了以后新点击就扔不进去了（"点了没反应"）
 *
 * 着色器那边还有一层收尾窗口，保证 `age = life` 时振幅正好归零，
 * 所以回收点不会造成跳变。见 `shaders.ts`。
 */
export function isAlive(disturbance: Disturbance, now: number): boolean {
  const age = now - disturbance.bornAt;
  return age >= 0 && age <= disturbance.life;
}
